import { observer } from 'mobx-react';
import styled, { useTheme } from 'styled-components';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { fluoroAnglesFromCamera } from '@/engine/utils/geometryHelper';
import type { IViewPortOrientation } from '@/engine/app/interfaces';
import type { FC } from 'react';
import type { OrientationPresetsController } from './OrientationPresets.controller';
import { Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

export const CurrentOrientationTableRow: FC<{
  controller: OrientationPresetsController;
  label: string;
  vpOrientation: IViewPortOrientation;
  anotherOrientation: IViewPortOrientation;
  isImagesVp: boolean;
}> = observer(({ controller, label, vpOrientation, anotherOrientation, isImagesVp }) => {
  const theme = useTheme();

  const isSelected =
    vpOrientation.topVector.angleTo(anotherOrientation.topVector) < 0.001 &&
    vpOrientation.directionVector.angleTo(anotherOrientation.directionVector) < 0.001;
  const currentAngles = fluoroAnglesFromCamera(vpOrientation.directionVector.clone().negate());
  const currentRaoLao = currentAngles.raoLao ?? 0;
  const currentCaudCran = currentAngles.caudCran ?? 0;
  const currentWarning = Math.abs(currentAngles.raoLao) > 90 + 1e-4 || Math.abs(currentAngles.caudCran) > 90 + 1e-4;

  return (
    <TableRow
      sx={{
        '&:last-child td, &:last-child th': { border: 0 },
        '& td': {
          paddingLeft: 0.1,
          paddingRight: 0.1,
          paddingTop: 0.5,
          paddingBottom: 0.5,
          height: 42
        },
        '& td:first-child': { paddingLeft: 2 },
        '& td:last-child': { paddingLeft: 0.1, paddingRight: 0.6 },
        '&:hover': {
          backgroundColor: theme.customPalette.panels.tableRowHighlighted,
          color: theme.contrastFont ?? 'inherit'
        }
      }}
      selected={isSelected}
      onClick={(): void => {
        controller.applyOrientation(vpOrientation, isImagesVp, !isImagesVp);
      }}
    >
      <TableCell sx={{ width: '12em' }}>{label}</TableCell>
      <TableCell sx={{ width: '8em' }}>
        <AnglesLabelContainer $warning={currentWarning}>
          <span>
            {currentRaoLao >= 0 ? 'LAO' : 'RAO'}: {Math.abs(currentRaoLao).toFixed() + '°'}
          </span>
        </AnglesLabelContainer>
      </TableCell>
      <TableCell sx={{ width: '9em' }}>
        <AnglesLabelContainer $warning={currentWarning}>
          <span>
            {currentCaudCran >= 0 ? 'CRAN' : 'CAUD'}: {Math.abs(currentCaudCran).toFixed() + '°'}
          </span>
        </AnglesLabelContainer>
      </TableCell>
    </TableRow>
  );
});

export const AddCurrentOrientationTableRow: FC<{
  controller: OrientationPresetsController;
  vpOrientation: IViewPortOrientation;
  isImagesVp: boolean;
}> = observer(({ controller, vpOrientation, isImagesVp }) => {
  return (
    <TableRow
      sx={{
        '& td': {
          border: 0,
          paddingLeft: 0.5,
          paddingRight: 0,
          paddingTop: 0,
          paddingBottom: 0,
          height: 42
        }
      }}
    >
      <TableCell>
        <Button
          sx={{ width: '5em', height: '2.5em' }}
          variant="contained"
          startIcon={<AddIcon />}
          color="primary"
          onClick={(): void => {
            void controller.addPreset('Custom', vpOrientation);
            controller.applyOrientation(vpOrientation, isImagesVp, !isImagesVp);
          }}
        >
          ADD
        </Button>
      </TableCell>
    </TableRow>
  );
});

const AnglesLabelContainer = styled.span<{ $warning: boolean }>`
  color: ${(props): string => (props.$warning ? props.theme.palette.error.main : 'inherit')};
`;
